/**
 * Demo operator Applicant page — a static visual showcase of an operator
 * review for the fictional applicant "Juan Alvarez". Surfaced from the
 * authenticated footer (Demo → the /demo index) so the team can demo the
 * operator experience without provisioning a real case.
 *
 * Everything on this page is hardcoded. Decision / Note / Reminder actions
 * fire a "Demo mode — no changes saved" toast instead of hitting the backend.
 * Visual styles, spacing, and icons mirror src/pages/Applicant.tsx so this
 * reads like the real product.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  BellRing,
  CheckCircle2,
  Circle,
  Clock,
  FileText,
  Info,
  Mail,
  MessageSquarePlus,
  Send,
  Shield,
  ShieldCheck,
  Sparkles,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { initClarity } from "@/lib/clarity";
import { useOperatorVertical } from "@/hooks/useOperatorMe";
import {
  canShowApplicantRating,
  isRecruiting,
  showsApplicantRating,
  showsAiReviewSummary,
} from "@/lib/verticalCopy";
import { reviewDecisionCopy } from "@/lib/caseStatus";

const DEMO_TOAST = () => toast.info("Demo mode — no changes saved");

const APPLICANT = {
  name: "Juan Alvarez",
  invitedAt: "May 26, 2026 · 9:14 AM",
  submittedAt: "May 28, 2026 · 2:41 PM",
};

const AI_SUMMARY = {
  narrative:
    "Juan completed identity verification with a strong digital footprint. Connected accounts (LinkedIn, Google, work email) corroborate name, employer, and email tenure spanning 6+ years. A trusted vouch from a current employer was successfully completed. No mismatches across linked accounts.",
  suggestedRead:
    "Identity confidence is well-established. Recommend marking as Verified unless the review team has additional context.",
  generatedAt: "May 28, 2026 · 2:46 PM",
};

const CONFIDENCE = {
  headline: "Strong identity confidence",
  summary:
    "Multiple corroborated digital signals plus a completed human vouch. No conflicts detected across connected accounts.",
  connected: [
    "Name & email match across LinkedIn, Google, and Microsoft work account",
    "Email account history: 6+ years on primary address",
    "LinkedIn employment matches employer-issued email domain",
    "Trusted vouch completed by current manager (verified ID + email)",
  ],
  missing: [
    "No bank account connected (optional)",
  ],
};

const SOURCES: Array<{
  name: string;
  detail: string;
  tenure: string;
  status: "connected" | "confirmed";
  icon: typeof Mail;
}> = [
  { name: "LinkedIn", detail: "Senior Designer at Northwind Co.", tenure: "Connected · 7 yr history", status: "connected", icon: ShieldCheck },
  { name: "Google account", detail: "juan.alvarez@gmail.com", tenure: "Connected · 9 yr history", status: "connected", icon: Mail },
  { name: "Microsoft work account", detail: "j.alvarez@northwind.com", tenure: "Connected · 2 yr history", status: "connected", icon: Mail },
  { name: "Human vouch", detail: "Maria Chen · Director (current manager)", tenure: "Confirmed · ID + email verified", status: "confirmed", icon: ShieldCheck },
];

const TIMELINE: Array<{ label: string; at: string }> = [
  { label: "Verification link sent", at: "May 26, 2026 · 9:14 AM" },
  { label: "Applicant started", at: "May 26, 2026 · 9:18 AM" },
  { label: "Consent granted", at: "May 26, 2026 · 9:19 AM" },
  { label: "Email verified", at: "May 26, 2026 · 9:21 AM" },
  { label: "Social account verified (LinkedIn)", at: "May 26, 2026 · 9:24 AM" },
  { label: "Email history verified", at: "May 26, 2026 · 9:25 AM" },
  { label: "Vouch invited (Maria Chen)", at: "May 26, 2026 · 9:27 AM" },
  { label: "Vouch responded", at: "May 28, 2026 · 11:02 AM" },
  { label: "Verification submitted", at: "May 28, 2026 · 2:41 PM" },
];

function SectionCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Card className="bg-card shadow-sm border-border/60">
      <CardContent className="p-5 sm:p-6 space-y-4">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function DemoApplicant() {
  const navigate = useNavigate();
  useEffect(() => { initClarity(); }, []);

  // Issue #205 / platform#583. This page is a static showcase with no org of
  // its own, but it is reachable by any authenticated operator in every
  // non-production environment (it 404s ONLY in production — customer demos
  // run on non-production, which is exactly where this page is live). It
  // must follow the DEMO VIEWER's own vertical, the same source
  // src/pages/Applicant.tsx reads, and apply the same two predicates PR #204
  // / #206 added rather than always showing the leasing-flavoured rating.
  //
  // ⚠️ Held on `!verticalResolved` too, same reason as the live page: an
  // in-flight membership query reads as unknown (`verticalFrom(undefined)`),
  // which rates nobody, but a cached or mocked leasing answer must still not
  // paint a rating for a beat on cold load. The card title is held too.
  const { vertical, isResolved: verticalResolved } = useOperatorVertical();
  const ratesApplicant = showsApplicantRating(vertical);
  const showsAiSummaryCard = showsAiReviewSummary(vertical);
  // The next-step line and the decision buttons use the same recruiting words
  // as the live page. Until the vertical resolves they use the recruiting
  // words too, which carry no verdict, same as the live page.
  const recruitingReview = !verticalResolved || isRecruiting(vertical);
  const recruitingCopy = reviewDecisionCopy("hr_recruiting");

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
      {/* Demo banner */}
      <div className="rounded-lg border border-violet-300/60 bg-violet-50/70 px-4 py-3 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-violet-700 shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1">
          <div className="text-sm font-semibold text-violet-900">Demo mode · Operator View</div>
          <p className="text-xs text-violet-900/80 mt-0.5">
            This is a sample applicant profile for demonstration. No actions are saved.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate("/demo")}
          className="shrink-0 bg-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Exit demo
        </Button>
      </div>

      <button
        onClick={() => navigate("/demo")}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to demos
      </button>

      {/* Header */}
      <motion.header initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">{APPLICANT.name}</h1>
          <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-violet-100 text-violet-900 border border-violet-200">
            Needs Review
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Shield className="w-3 h-3" /> Sample data
          </span>
        </div>
        {/* The locator and verification-reason lines are gone. "Unit /
            Property: Unit 4B · DV Properties" is the LEASING locator —
            `verticalCopy` calls that field "Role" for recruiting — so on a
            recruiting walkthrough this header described a unit in a building.
            Dropping both rather than forking them: this page is a static
            showcase with no session and no vertical to read, so a forked
            locator would be one more hardcoded guess. */}
        <p className="text-xs text-muted-foreground mt-1">
          Invited {APPLICANT.invitedAt} · Submitted {APPLICANT.submittedAt}
        </p>
      </motion.header>

      {/* AI Review Summary.
          platform#600 part 1, same gate src/pages/Applicant.tsx uses: hidden
          entirely for recruiting, and held on `!verticalResolved` too so a
          cold load never flashes the narrative at a recruiting operator. */}
      {(!verticalResolved || showsAiSummaryCard) && (
        <SectionCard label="AI Review Summary">
          {!verticalResolved ? (
            <div className="border-l-4 border-border pl-4 space-y-2">
              <div className="h-3 w-24 bg-muted rounded animate-pulse" />
              <div className="h-3 w-full bg-muted rounded animate-pulse" />
              <div className="h-3 w-5/6 bg-muted rounded animate-pulse" />
            </div>
          ) : (
            <div className="border-l-4 border-primary/70 pl-4">
              <div className="text-sm font-semibold text-foreground mb-2">Summary</div>
              <p className="text-sm text-foreground leading-relaxed">{AI_SUMMARY.narrative}</p>
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-wider text-primary mb-1.5">Suggested read</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{AI_SUMMARY.suggestedRead}</p>
              </div>
              <div className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-primary/70" aria-hidden />
                <span>AI Summary · {AI_SUMMARY.generatedAt}</span>
              </div>
            </div>
          )}
        </SectionCard>
      )}

      {/* Identity Confidence / Identity Evidence.
          platform#583, same gate as src/pages/Applicant.tsx: leasing leads
          with the rating; recruiting leads with the evidence and no rating at
          all. The CONNECTED / MISSING grids are identical in both — they are
          the evidence, and the evidence was never in question. */}
      <SectionCard label={canShowApplicantRating(vertical, verticalResolved) ? "Identity Confidence" : "Identity Evidence"}>
        {!verticalResolved ? (
          <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
            <div className="h-4 w-40 bg-muted rounded animate-pulse" />
            <div className="h-3 w-3/4 bg-muted rounded animate-pulse" />
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="h-20 bg-muted/60 rounded animate-pulse" />
              <div className="h-20 bg-muted/60 rounded animate-pulse" />
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
            {ratesApplicant && (
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-base font-bold text-sky-900">{CONFIDENCE.headline}</div>
                  <p className="text-sm text-foreground mt-1 max-w-xl">{CONFIDENCE.summary}</p>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-sky-100 text-sky-800">
                  AI-assisted recommendation
                </span>
              </div>
            )}
            <div className={`grid sm:grid-cols-2 gap-3 ${ratesApplicant ? "mt-4" : ""}`}>
            <div className="rounded-lg bg-card border border-emerald-200 p-3">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1.5">CONNECTED</div>
              <ul className="text-sm text-foreground space-y-1.5">
                {CONFIDENCE.connected.map((w, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg bg-card border border-border p-3">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">MISSING</div>
              <ul className="text-sm text-foreground space-y-1.5">
                {CONFIDENCE.missing.map((m, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Circle className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          </div>
        )}
      </SectionCard>

      {/* Recommended Next Step */}
      <SectionCard label="Recommended Next Step">
        <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-bold text-sky-900">Ready for team review</div>
            <p className="text-sm text-foreground mt-1">
              {recruitingReview
                ? "Open the Identity Evidence card to see what the applicant provided and what is still missing. When you are done, record your review below."
                : "Identity confidence is established across multiple sources. Mark as Verified to release the case back to the review team."}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              You can still request more verification or close the case if additional context is needed.
            </p>
          </div>
        </div>
      </SectionCard>

      {/* Connected Sources */}
      <SectionCard label="Connected Sources">
        <ul className="divide-y divide-border">
          {SOURCES.map((s, i) => {
            const Icon = s.icon;
            const badge = s.status === "confirmed"
              ? "bg-sky-50 text-sky-800 border-sky-200"
              : "bg-emerald-50 text-emerald-800 border-emerald-200";
            return (
              <li key={i} className="py-3 flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-foreground/70" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-foreground">{s.name}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${badge}`}>
                      {s.status === "confirmed" ? "Confirmed" : "Connected"}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{s.detail}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{s.tenure}</div>
                </div>
              </li>
            );
          })}
        </ul>
      </SectionCard>

      {/* Activity Timeline */}
      <SectionCard label="Activity">
        <ol className="space-y-2">
          {TIMELINE.map((ev, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" aria-hidden />
              <div className="flex-1 flex items-center justify-between gap-3 flex-wrap">
                <span className="text-foreground">{ev.label}</span>
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {ev.at}
                </span>
              </div>
            </li>
          ))}
        </ol>
      </SectionCard>

      {/* Review Recommendation — primary decision row (top-left layout rule per case-review-layout memory) */}
      <SectionCard label={recruitingReview ? "Complete Review" : "Review Recommendation"}>
        <div className="grid sm:grid-cols-3 gap-3">
          <Button onClick={DEMO_TOAST} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <CheckCircle2 className="w-4 h-4 mr-1" /> {recruitingReview ? recruitingCopy.proceedLabel : "Mark Verified"}
          </Button>
          <Button onClick={DEMO_TOAST} variant="outline">
            <MessageSquarePlus className="w-4 h-4 mr-1" /> Request More Verification
          </Button>
          <Button onClick={DEMO_TOAST} variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50">
            <XCircle className="w-4 h-4 mr-1" /> {recruitingReview ? recruitingCopy.holdLabel : "Mark Not Verified"}
          </Button>
        </div>
      </SectionCard>

      {/* Other Actions */}
      <SectionCard label="Other Actions">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={DEMO_TOAST}>
            <Send className="w-4 h-4 mr-1" /> Resend Verification Link
          </Button>
          <Button size="sm" variant="ghost" onClick={DEMO_TOAST}>
            <BellRing className="w-4 h-4 mr-1" /> Send Reminder
          </Button>
          <Button size="sm" variant="ghost" onClick={DEMO_TOAST}>
            <FileText className="w-4 h-4 mr-1" /> Add Internal Note
          </Button>
          <Button size="sm" variant="ghost" onClick={DEMO_TOAST}>
            <XCircle className="w-4 h-4 mr-1" /> Close Case
          </Button>
        </div>
      </SectionCard>
    </div>
  );
}
